// The shifts the private trip and Uber validation work could not settle either
// way, grouped by what makes them uncertain.
//
// Luyanda: "Please also show me the shifts you were unsure about."
//
// WHAT THIS IS FOR. A private trip above the R1,000 review ceiling is held, not
// credited, and until 6 September a held shift looked exactly like a shift with
// no private trip on it. That is the worst possible presentation of a deliberate
// decision: the money is off an owner's statement and nothing on any screen says
// so, which means nobody ever comes back to it. Each row carries what was seen
// and what would settle it, because a queue that says "unsure" without saying
// what would resolve it is a queue that never empties.
//
// The order is by what a person can do, not by rand. The two largest groups by
// count are the two nobody can decide today: a driver with no Uber identity on
// file, and a shift whose submitted figure was overwritten before it was kept.
// Both are a load or a link, so they sit at the bottom.
import { useMemo } from 'react'
import { ChevronRight } from 'lucide-react'
import { formatZar, formatInt, formatDate } from '../../utils/statements'
import { cn } from '../../lib/shadcn/utils'

export type UncertainShift = {
  uncertainty: string
  sort_order: number
  shift_id: string
  shift_date: string
  plate: string | null
  driver_name: string | null
  zar_at_stake: number | null
  kilometres: number | null
  uber_zar_per_km: number | null
  what_i_saw: string
  what_would_settle_it: string
  bucket_shifts: number
  bucket_zar: number
}

type Group = {
  key: string
  label: string
  sort_order: number
  rows: UncertainShift[]
  bucket_shifts: number
  bucket_zar: number
  settle: string
}

export function UncertainShiftQueue({ rows }: { rows: UncertainShift[] }) {
  const groups = useMemo<Group[]>(() => {
    const map = new Map<string, Group>()
    for (const r of rows) {
      let g = map.get(r.uncertainty)
      if (!g) {
        g = {
          key: r.uncertainty,
          label: r.uncertainty,
          sort_order: r.sort_order,
          rows: [],
          bucket_shifts: r.bucket_shifts,
          bucket_zar: r.bucket_zar,
          settle: r.what_would_settle_it,
        }
        map.set(r.uncertainty, g)
      }
      g.rows.push(r)
    }
    return Array.from(map.values()).sort((a, b) => a.sort_order - b.sort_order)
  }, [rows])

  if (groups.length === 0) {
    return (
      <div className="rounded-md border border-border bg-background p-6 text-center text-sm text-muted-foreground">
        Nothing is being held. Every shift either has a figure Uber stands behind or a
        private trip that has been settled.
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-5">
      {groups.map((g) => {
        const capped = g.rows.length < g.bucket_shifts
        return (
          <div key={g.key} className="rounded-lg border border-border bg-background">
            <div className="border-b border-border px-3 py-2">
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                <span className="text-sm font-semibold text-foreground">{g.label}</span>
                <span className="text-xs tabular-nums text-muted-foreground">
                  {formatInt(g.bucket_shifts)} shift{g.bucket_shifts === 1 ? '' : 's'}
                  {g.bucket_zar > 0 ? ` · ${formatZar(g.bucket_zar)} at stake` : ''}
                </span>
                {capped && (
                  <span className="text-[11px] text-muted-foreground">
                    showing the largest {formatInt(g.rows.length)}
                  </span>
                )}
              </div>
              <p className="mt-1 flex items-start gap-1 text-[11px] leading-snug text-muted-foreground">
                <ChevronRight className="mt-0.5 h-3 w-3 shrink-0" />
                <span>{g.settle}</span>
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                    <th className="px-3 py-2 font-medium">Date</th>
                    <th className="px-3 py-2 font-medium">Vehicle</th>
                    <th className="px-3 py-2 font-medium">Driver</th>
                    <th className="px-3 py-2 text-right font-medium">At stake</th>
                    <th className="px-3 py-2 text-right font-medium">Km</th>
                    <th className="px-3 py-2 text-right font-medium">Uber R/km</th>
                    <th className="px-3 py-2 font-medium">What I saw</th>
                  </tr>
                </thead>
                <tbody>
                  {g.rows.map((r) => (
                    <tr key={r.shift_id} className="border-b border-border/60 align-top last:border-0">
                      <td className="whitespace-nowrap px-3 py-2">{formatDate(r.shift_date)}</td>
                      <td className="whitespace-nowrap px-3 py-2 font-medium">{r.plate ?? '—'}</td>
                      <td className="px-3 py-2">{r.driver_name ?? '—'}</td>
                      <td
                        className={cn(
                          'px-3 py-2 text-right tabular-nums',
                          (r.zar_at_stake ?? 0) > 0 && 'font-semibold',
                        )}
                      >
                        {r.zar_at_stake === null ? '—' : formatZar(r.zar_at_stake)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {r.kilometres === null ? '—' : formatInt(r.kilometres)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {r.uber_zar_per_km === null ? '—' : r.uber_zar_per_km.toFixed(2)}
                      </td>
                      <td className="max-w-[420px] px-3 py-2 text-xs leading-snug text-muted-foreground">
                        {r.what_i_saw}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      })}
    </div>
  )
}
